import React from 'react';

// Utils
import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { getSectionPhoto } from '../../../util/pageAssets';

// Shared components
import {
  MarketingButton,
  MarketingCards,
  MarketingFaq,
  MarketingHero,
  MarketingHeroPoints,
  MarketingPanel,
  MarketingSection,
  MarketingSectionHeader,
  MarketingSteps,
  MarketingTextLink,
} from '../../../components';

// Parent directory
import { SearchCTA } from '../../PageBuilder/Primitives/SearchCTA/SearchCTA';

// Same directory
import css from './DriveloHome.module.css';

/**
 * Homepage questions, in display order. Each key has a question and an answer translation:
 * `LandingPage.faq{Key}Question` and `LandingPage.faq{Key}Answer`.
 */
export const FAQ_KEYS = ['Cost', 'Cancel', 'Verified', 'Suburbs', 'Employees', 'Instructor'];

const WHY_ITEMS = [
  { key: 'Verified', icon: 'shield' },
  { key: 'Price', icon: 'receipt' },
  { key: 'Car', icon: 'car' },
  { key: 'Reviews', icon: 'star' },
];

const HERO_POINTS = ['Verified', 'Price', 'Refund'];
const STEPS = [1, 2, 3];

/**
 * Drivelo homepage content: hero search, why Drivelo, how it works, instructor sign-up and
 * common questions. Copy comes from translations; photos come from the Console landing-page
 * asset (hero section and the features section) and are left out when missing.
 *
 * @component
 * @param {Object} props
 * @param {Array<Object>?} props.sections sections of the Console landing-page asset
 * @returns {JSX.Element}
 */
const DriveloHome = props => {
  const { sections } = props;
  const intl = useIntl();
  const message = id => <FormattedMessage id={`LandingPage.${id}`} />;

  return (
    <>
      <MarketingHero
        id="home-hero-title"
        photo={getSectionPhoto(sections, 'hero')}
        title={message('heroTitle')}
        subtitle={message('heroSubtitle')}
      >
        <SearchCTA searchFields={{ locationSearch: true }} />
        <MarketingHeroPoints
          items={HERO_POINTS.map(key => ({ key, text: message(`heroPoint${key}`) }))}
        />
      </MarketingHero>

      <MarketingSection labelledBy="home-why" tinted>
        <MarketingSectionHeader
          id="home-why"
          title={message('whyTitle')}
          subtitle={message('whySubtitle')}
        />
        <MarketingCards
          items={WHY_ITEMS.map(({ key, icon }) => ({
            key,
            icon,
            title: message(`why${key}Title`),
            text: message(`why${key}Text`),
          }))}
        />
      </MarketingSection>

      <MarketingSection labelledBy="home-how">
        <MarketingSectionHeader
          id="home-how"
          title={message('howTitle')}
          subtitle={message('howSubtitle')}
        />
        <MarketingSteps
          items={STEPS.map(step => ({
            key: `${step}`,
            title: message(`howStep${step}Title`),
            text: message(`howStep${step}Text`),
          }))}
        />
        <div className={css.ctaRow}>
          <MarketingButton name="SearchPage">{message('howCta')}</MarketingButton>
        </div>
      </MarketingSection>

      <MarketingPanel
        id="home-instructors"
        photo={getSectionPhoto(sections, 'features')}
        eyebrow={message('instructorEyebrow')}
        title={message('instructorTitle')}
        text={message('instructorText')}
        action={
          <MarketingButton name="CMSPage" params={{ pageId: 'become-an-instructor' }}>
            {message('instructorCta')}
          </MarketingButton>
        }
      />

      <MarketingFaq
        id="home-faq"
        title={message('faqTitle')}
        subtitle={message('faqSubtitle')}
        link={
          <MarketingTextLink name="CMSPage" params={{ pageId: 'faq' }}>
            {message('faqAllLink')}
          </MarketingTextLink>
        }
        items={FAQ_KEYS.map(key => ({
          key,
          question: intl.formatMessage({ id: `LandingPage.faq${key}Question` }),
          answer: intl.formatMessage({ id: `LandingPage.faq${key}Answer` }),
        }))}
      />
    </>
  );
};

export default DriveloHome;
